import React from 'react';
import { Box, Tooltip } from '@mui/material';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import { EmojiType } from '../types';
import { getEmojiConfig } from '../utils/emojiUtils';

/**
 * Display component for showing emojis in survey responses.
 * Used in SingleSelectQuestion and MultiSelectQuestion components.
 */
interface OptionEmojiProps {
  emoji: EmojiType;
  color?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showTooltip?: boolean;
}

/**
 * Maps emoji types to their corresponding MUI icons.
 */
const EMOJI_ICON_MAP: Record<EmojiType, React.ElementType> = {
  very_satisfied: SentimentVerySatisfiedIcon,
  satisfied: SentimentSatisfiedIcon,
  neutral: SentimentNeutralIcon,
  dissatisfied: SentimentDissatisfiedIcon,
  very_dissatisfied: SentimentVeryDissatisfiedIcon,
};

/**
 * Gets the size in pixels based on the size prop.
 */
const getSizeInPx = (size: 'small' | 'medium' | 'large' | 'xlarge'): number => {
  switch (size) {
    case 'small':
      return 20;
    case 'large':
      return 32;
    case 'xlarge':
      return 52;
    case 'medium':
    default:
      return 24;
  }
};

export const OptionEmoji: React.FC<OptionEmojiProps> = ({
  emoji,
  color,
  size = 'medium',
  showTooltip = false,
}) => {
  const IconComponent = EMOJI_ICON_MAP[emoji];
  const config = getEmojiConfig(emoji);
  const sizeInPx = getSizeInPx(size);

  if (!IconComponent) {
    return null;
  }

  const icon = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-label={config?.label}
    >
      <IconComponent
        sx={{
          // Use explicit width/height for crisper SVG rendering
          width: sizeInPx,
          height: sizeInPx,
          fontSize: sizeInPx,
          color: color || 'primary.main',
          // SVG rendering hints for sharper edges
          '& path': {
            shapeRendering: 'geometricPrecision',
          },
        }}
      />
    </Box>
  );

  if (showTooltip && config) {
    return (
      <Tooltip title={config.label} arrow placement="top">
        {icon}
      </Tooltip>
    );
  }

  return icon;
};
